import { useEffect, useState } from "react";
import { CarScreenViewport } from "./CarScreenViewport";
import { NightAccidentGuideOverlay } from "./NightAccidentGuideOverlay";
import { NightAiCard } from "./NightAiCard";
import { NightAiExpandedCard } from "./NightAiExpandedCard";
import { NightAudioCompanionDemo } from "./NightAudioCompanionDemo";
import { NightBottomCards } from "./NightBottomCards";
import { NightCarwashModeCard } from "./NightCarwashModeCard";
import { NightChargingSessionCard } from "./NightChargingSessionCard";
import { NightDock } from "./NightDock";
import { NightFloatingShortcut } from "./NightFloatingShortcut";
import { NightHeroLayer } from "./NightHeroLayer";
import { NightParcelExitReminderCard } from "./NightParcelExitReminderCard";
import { NightRefuelAuthorizationCard } from "./NightRefuelAuthorizationCard";
import { NightStatusBar, NightVoiceCaptionOverlay } from "./NightStatusBar";
import { NightVideoCompanionLayer } from "./NightVideoCompanionLayer";
import { ParcelDetailPopup } from "./ParcelDetailPopup";
import { TraditionalDesktopAmapLayer } from "./TraditionalDesktopAmapLayer";
import {
  type TraditionalDesktopDestinationTarget
} from "./TraditionalDesktopMapDestination";
import {
  TraditionalDesktopCarwashNavigationCard,
  TraditionalDesktopParcelNavigationCard,
  TraditionalDesktopRefuelNavigationCard,
  TraditionalDesktopMapNavigationCard,
  type TraditionalCarwashFlowStage,
  type TraditionalChargingFlowStage,
  type TraditionalParcelFlowStage,
  type TraditionalRefuelFlowStage
} from "./TraditionalDesktopMapNavigationCard";
import {
  FALLBACK_CHARGING_STATIONS,
  type ChargingStation,
  type GeoPoint,
  getRecommendedStation
} from "../../config/amapChargingDemo";
import {
  getRecommendedCarwashShop,
  type CarwashShop,
} from "../../config/amapCarwashDemo";
import {
  getRecommendedFuelStation,
  type FuelStation
} from "../../config/amapFuelDemo";
import type { CarwashModeState } from "../../config/carwashMode";
import {
  getPickupPointDisplayName,
  getRecommendedParcelPickupPoint,
} from "../../config/amapParcelDemo";
import type { ChargingSessionState } from "../../config/chargingSession";
import type {
  AudioCompanionDemoState,
  AudioCompanionProgramId
} from "../../config/audioCompanionDemo";
import type {
  VideoCompanionDemoState,
  VideoCompanionVideoId
} from "../../config/videoCompanionDemo";
import type { ParcelExitReminderState } from "../../config/parcelExitReminder";
import type { RefuelAuthorizationState } from "../../config/refuelAuthorization";
import {
  type CockpitData,
  useCockpitData,
  useCockpitRouteGeometry
} from "../../context/CockpitDataContext";
import { NIGHT_ASSETS } from "../../config/figmaNightLayout";
import { useSystemClock } from "../../hooks/useSystemClock";
import type { VoiceDestinationSearchRequest } from "../../logic/voiceCommand";
import type { CabinState, DecisionSummary, ServiceId } from "../../types/domain";
import { assetUrl } from "../../utils/assetUrl";

export function DesktopStage({
  state,
  summary,
  selectedService,
  voiceDestinationSearchRequest,
  voiceCaption,
  audioCompanionDemo,
  videoCompanionDemo,
  carwashMode,
  chargingSession,
  parcelExitReminder,
  parkingPaymentOpen,
  refuelAuthorization,
  accidentGuideOpen,
  accidentCallVisible,
  onOpenService,
  onCloseService,
  onConfirmAccidentGuide,
  onHangupAccidentCall,
  onCarwashArriveShop,
  onAuthorizeRefuel,
  onConfirmCarwashMode,
  onConfirmService,
  onConfirmChargingCover,
  onDismissCarwashMode,
  onDismissChargingSession,
  onDismissParcelExitReminder,
  onDismissRefuelAuthorization,
  onEnableChargingRestMode,
  onParcelArriveHome,
  onRefuelArriveStation,
  onAudioCompanionAnswerFinished,
  onAudioCompanionBackToRecommend,
  onAudioCompanionExpandPreview,
  onAudioCompanionFallbackSpeak,
  onAudioCompanionPreviewProgram,
  onAudioCompanionSelectProgram,
  onAudioCompanionStartGeneratedProgram,
  onVideoCompanionActorFinished,
  onVideoCompanionAskActor,
  onVideoCompanionOpenQuicklook,
  onVideoCompanionPlaybackEnded,
  onVideoCompanionReset,
  onVideoCompanionSelectVideo,
  onShowChargingRestRecommendation,
  onStartChargingSession,
  onCloseParkingPayment,
  onParkingPaymentPaid
}: {
  state: CabinState;
  summary: DecisionSummary;
  selectedService: ServiceId | null;
  voiceDestinationSearchRequest: VoiceDestinationSearchRequest | null;
  voiceCaption?: {
    active: boolean;
    phase: string;
    text: string;
  };
  audioCompanionDemo: AudioCompanionDemoState | null;
  videoCompanionDemo: VideoCompanionDemoState | null;
  carwashMode: CarwashModeState;
  chargingSession: ChargingSessionState;
  parcelExitReminder: ParcelExitReminderState;
  parkingPaymentOpen: boolean;
  refuelAuthorization: RefuelAuthorizationState;
  accidentGuideOpen: boolean;
  accidentCallVisible: boolean;
  onOpenService: (serviceId: ServiceId) => void;
  onCloseService: () => void;
  onConfirmAccidentGuide: () => void;
  onHangupAccidentCall: () => void;
  onCarwashArriveShop: (shop: CarwashShop) => void;
  onAuthorizeRefuel: () => void;
  onConfirmCarwashMode: () => void;
  onConfirmService: () => void;
  onConfirmChargingCover: () => void;
  onDismissCarwashMode: () => void;
  onDismissChargingSession: () => void;
  onDismissParcelExitReminder: () => void;
  onDismissRefuelAuthorization: () => void;
  onEnableChargingRestMode: () => void;
  onParcelArriveHome: (payload: { pendingCount: number; pickupPointName: string }) => void;
  onRefuelArriveStation: (station: FuelStation) => void;
  onAudioCompanionAnswerFinished: () => void;
  onAudioCompanionBackToRecommend: () => void;
  onAudioCompanionExpandPreview: () => void;
  onAudioCompanionFallbackSpeak: (text: string) => void;
  onAudioCompanionPreviewProgram: (programId: AudioCompanionProgramId) => void;
  onAudioCompanionSelectProgram: (programId: AudioCompanionProgramId) => void;
  onAudioCompanionStartGeneratedProgram: () => void;
  onVideoCompanionActorFinished: () => void;
  onVideoCompanionAskActor: () => void;
  onVideoCompanionOpenQuicklook: () => void;
  onVideoCompanionPlaybackEnded: () => void;
  onVideoCompanionReset: () => void;
  onVideoCompanionSelectVideo: (videoId: VideoCompanionVideoId, nextStage?: VideoCompanionDemoState["stage"]) => void;
  onShowChargingRestRecommendation: () => void;
  onStartChargingSession: (station: ChargingStation) => void;
  onCloseParkingPayment: () => void;
  onParkingPaymentPaid: () => void;
}) {
  const [parcelDetailOpen, setParcelDetailOpen] = useState(false);
  const [traditionalCardCollapsed, setTraditionalCardCollapsed] = useState(false);
  const [traditionalDestinationTarget, setTraditionalDestinationTarget] =
    useState<TraditionalDesktopDestinationTarget>("restaurant");
  const [traditionalChargingFlowStage, setTraditionalChargingFlowStage] =
    useState<TraditionalChargingFlowStage>("selection");
  const [selectedChargingStationId, setSelectedChargingStationId] = useState(FALLBACK_CHARGING_STATIONS[0].id);
  const [chargingStationManuallySelected, setChargingStationManuallySelected] = useState(false);
  const [traditionalCarwashCardCollapsed, setTraditionalCarwashCardCollapsed] = useState(false);
  const [traditionalCarwashFlowStage, setTraditionalCarwashFlowStage] =
    useState<TraditionalCarwashFlowStage>("selection");
  const [selectedCarwashShopId, setSelectedCarwashShopId] = useState("");
  const [carwashShopManuallySelected, setCarwashShopManuallySelected] = useState(false);
  const [traditionalRefuelCardCollapsed, setTraditionalRefuelCardCollapsed] = useState(false);
  const [traditionalRefuelFlowStage, setTraditionalRefuelFlowStage] =
    useState<TraditionalRefuelFlowStage>("selection");
  const [selectedFuelStationId, setSelectedFuelStationId] = useState("");
  const [fuelStationManuallySelected, setFuelStationManuallySelected] = useState(false);
  const [traditionalParcelCardCollapsed, setTraditionalParcelCardCollapsed] = useState(false);
  const [traditionalParcelFlowStage, setTraditionalParcelFlowStage] =
    useState<TraditionalParcelFlowStage>("selection");
  const [selectedParcelPointId, setSelectedParcelPointId] = useState("");
  const [destinationSearchServiceId, setDestinationSearchServiceId] = useState<ServiceId | null>(null);
  const cockpitData = useCockpitData();
  const destinationName = cockpitData.charging.destination.name;
  const destinationLocation = cockpitData.charging.destination.location;
  const carwashShops = cockpitData.carwash.shops;
  const carwashShopSource = cockpitData.carwash.source;
  const chargingStations = cockpitData.charging.stations;
  const chargingStationSource = cockpitData.charging.source;
  const fuelStations = cockpitData.fuel.stations;
  const fuelStationSource = cockpitData.fuel.source;
  const parcelPickupPoints = cockpitData.parcel.pickupPoints;
  const parcelPointSource = cockpitData.parcel.source;
  const selectedParcelPoint =
    parcelPickupPoints.find((point) => point.id === selectedParcelPointId) ??
    cockpitData.parcel.pickupPoint;
  const selectedCarwashShop =
    carwashShops.find((shop) => shop.id === selectedCarwashShopId) ??
    cockpitData.carwash.recommendedShop;
  const selectedFuelStation =
    fuelStations.find((station) => station.id === selectedFuelStationId) ??
    cockpitData.fuel.recommendedStation;
  const systemTime = useSystemClock();
  const isAudioCompanionVisible = Boolean(audioCompanionDemo && state.desktop === "ai");
  const isVideoCompanionVisible = Boolean(videoCompanionDemo && state.scriptTrigger === "video-companion" && state.desktop === "ai");
  const isCompanionVisible = isAudioCompanionVisible || isVideoCompanionVisible;
  const displayedTime = isAudioCompanionVisible ? "08:15" : isVideoCompanionVisible ? "18:40" : systemTime;
  const wallpaperSrc = assetUrl(NIGHT_ASSETS.wallpaper);
  const topDockSrc = assetUrl(NIGHT_ASSETS.topDock);
  const avatarSrc = assetUrl(NIGHT_ASSETS.avatar);
  const floatingShortcutSrc = assetUrl(NIGHT_ASSETS.floatingShortcut);
  const traditionalDesktopSrc = assetUrl(NIGHT_ASSETS.traditionalDesktop);
  const traditionalDockSrc = assetUrl(NIGHT_ASSETS.traditionalDock);
  const traditionalCarwashNavigationSrc = assetUrl(NIGHT_ASSETS.traditionalCarwashNavigation);
  const traditionalParcelNavigationSrc = assetUrl(NIGHT_ASSETS.traditionalParcelNavigation);
  const traditionalChargingNavigationSrc = assetUrl(NIGHT_ASSETS.traditionalChargingNavigation);
  const evaFrontSrc = assetUrl(NIGHT_ASSETS.evaFront);
  const evaLookDownSrc = assetUrl(NIGHT_ASSETS.evaLookDown);
  const evaAlertSrc = assetUrl(NIGHT_ASSETS.evaStand);
  const parkingCardSrc = assetUrl(NIGHT_ASSETS.parkingCard);
  const musicCardSrc = assetUrl(NIGHT_ASSETS.musicCard);
  const selectedDecision = selectedService
    ? summary.decisions.find((decision) => decision.scenario.id === selectedService) ?? null
    : null;
  const isChargingSessionVisible = chargingSession.stage !== "idle";
  const isCarwashModeVisible = carwashMode.stage !== "idle";
  const isParcelExitReminderVisible = parcelExitReminder.visible;
  const isRefuelAuthorizationVisible = refuelAuthorization.stage !== "idle";
  const hasNightPrompt = Boolean(isCompanionVisible || summary.activeDecision || selectedDecision || isCarwashModeVisible || isChargingSessionVisible || isParcelExitReminderVisible || isRefuelAuthorizationVisible || accidentGuideOpen);
  const isNightCardExpanded = Boolean(isCompanionVisible || selectedDecision || isCarwashModeVisible || isChargingSessionVisible || isParcelExitReminderVisible || isRefuelAuthorizationVisible || accidentGuideOpen);
  const activeServiceId = summary.activeDecision?.scenario.id ?? (state.desktop === "standard" ? selectedService : null);
  const shouldShowTraditionalRefuelCard = activeServiceId === "refuel" && (state.fuelLevel <= 30 || state.fuelRangeKm <= 240);
  const traditionalNavigationSrc = getTraditionalNavigationSrc({
    activeServiceId,
    carwashNavigationSrc: traditionalCarwashNavigationSrc,
    chargingNavigationSrc: traditionalChargingNavigationSrc,
    hasCarwashInput: state.carwashCoupon,
    hasChargingRoute: state.batteryLevel <= 28 && state.chargerAvailability >= 4,
    hasParcelRoute: state.parcelPending && state.parcelOnRoute,
    parcelNavigationSrc: traditionalParcelNavigationSrc
  });
  const shouldShowTraditionalChargingCard = !shouldShowTraditionalRefuelCard && traditionalNavigationSrc === traditionalChargingNavigationSrc;
  const shouldShowTraditionalCarwashCard = !shouldShowTraditionalRefuelCard && traditionalNavigationSrc === traditionalCarwashNavigationSrc;
  const shouldShowTraditionalParcelCard = !shouldShowTraditionalRefuelCard && traditionalNavigationSrc === traditionalParcelNavigationSrc;
  const hasTraditionalNavigation = Boolean(traditionalNavigationSrc || shouldShowTraditionalRefuelCard);
  const hasDestinationSearchRoute =
    state.desktop === "standard" &&
    destinationSearchServiceId === activeServiceId;
  const selectedChargingStation =
    chargingStations.find((station) => station.id === selectedChargingStationId) ??
    cockpitData.charging.recommendedStation;
  const routeStartsAtChargingStation =
    traditionalChargingFlowStage === "arrivedCharging" ||
    traditionalChargingFlowStage === "toDestination";
  const routeDestinationTarget: TraditionalDesktopDestinationTarget = routeStartsAtChargingStation
    ? "restaurant"
    : hasDestinationSearchRoute
      ? "restaurant"
    : shouldShowTraditionalParcelCard
      ? "parcel"
      : shouldShowTraditionalCarwashCard
        ? "carwash"
      : shouldShowTraditionalRefuelCard
        ? "refuel"
      : traditionalDestinationTarget;
  const routeOriginOverride = routeStartsAtChargingStation ? selectedChargingStation.location : undefined;
  const routeGeometry = useCockpitRouteGeometry({
    originOverride: routeOriginOverride,
    selectedCarwashShop,
    selectedFuelStation,
    selectedParcelPoint,
    selectedStation: selectedChargingStation,
    target: routeDestinationTarget
  });
  const displayedVehicle = routeStartsAtChargingStation
    ? createStationVehicleState(cockpitData.vehicle, selectedChargingStation.location)
    : cockpitData.vehicle;

  useEffect(() => {
    if (state.desktop !== "ai" || state.liteMode || (activeServiceId !== "parcel" && selectedService !== "parcel")) {
      setParcelDetailOpen(false);
    }
  }, [activeServiceId, selectedService, state.desktop, state.liteMode]);

  useEffect(() => {
    if (!shouldShowTraditionalChargingCard) {
      setTraditionalCardCollapsed(false);
      setTraditionalDestinationTarget("restaurant");
      setTraditionalChargingFlowStage("selection");
      setSelectedChargingStationId(cockpitData.charging.recommendedStation.id);
      setChargingStationManuallySelected(false);
    }
  }, [cockpitData.charging.recommendedStation.id, shouldShowTraditionalChargingCard]);

  useEffect(() => {
    if (!shouldShowTraditionalCarwashCard) {
      setTraditionalCarwashCardCollapsed(false);
      setTraditionalCarwashFlowStage("selection");
      setSelectedCarwashShopId(cockpitData.carwash.recommendedShop.id);
      setCarwashShopManuallySelected(false);
    }
  }, [cockpitData.carwash.recommendedShop.id, shouldShowTraditionalCarwashCard]);

  useEffect(() => {
    if (!shouldShowTraditionalRefuelCard) {
      setTraditionalRefuelCardCollapsed(false);
      setTraditionalRefuelFlowStage("selection");
      setSelectedFuelStationId(cockpitData.fuel.recommendedStation.id);
      setFuelStationManuallySelected(false);
    }
  }, [cockpitData.fuel.recommendedStation.id, shouldShowTraditionalRefuelCard]);

  useEffect(() => {
    if (!shouldShowTraditionalParcelCard) {
      setTraditionalParcelCardCollapsed(false);
      setTraditionalParcelFlowStage("selection");
      setSelectedParcelPointId(cockpitData.parcel.pickupPoint.id);
    }
  }, [cockpitData.parcel.pickupPoint.id, shouldShowTraditionalParcelCard]);

  useEffect(() => {
    const recommendedStationId = getRecommendedStation(chargingStations).id;

    if (chargingStationManuallySelected && chargingStations.some((station) => station.id === selectedChargingStationId)) {
      return;
    }

    setSelectedChargingStationId(recommendedStationId);
    setChargingStationManuallySelected(false);
  }, [chargingStationManuallySelected, chargingStations, selectedChargingStationId]);

  useEffect(() => {
    const recommendedShopId = getRecommendedCarwashShop(carwashShops).id;

    if (carwashShopManuallySelected && carwashShops.some((shop) => shop.id === selectedCarwashShopId)) {
      return;
    }

    setSelectedCarwashShopId(recommendedShopId);
    setCarwashShopManuallySelected(false);
  }, [carwashShopManuallySelected, carwashShops, selectedCarwashShopId]);

  useEffect(() => {
    if (parcelPickupPoints.some((point) => point.id === selectedParcelPointId)) {
      return;
    }

    setSelectedParcelPointId(getRecommendedParcelPickupPoint(parcelPickupPoints).id);
  }, [parcelPickupPoints, selectedParcelPointId]);

  useEffect(() => {
    const recommendedStationId = getRecommendedFuelStation(fuelStations).id;

    if (fuelStationManuallySelected && fuelStations.some((station) => station.id === selectedFuelStationId)) {
      return;
    }

    setSelectedFuelStationId(recommendedStationId);
    setFuelStationManuallySelected(false);
  }, [fuelStationManuallySelected, fuelStations, selectedFuelStationId]);

  useEffect(() => {
    if (state.desktop !== "standard") {
      setDestinationSearchServiceId(null);
      setTraditionalChargingFlowStage("selection");
      setTraditionalCardCollapsed(false);
      setTraditionalDestinationTarget("restaurant");
      setChargingStationManuallySelected(false);
      setTraditionalCarwashFlowStage("selection");
      setTraditionalCarwashCardCollapsed(false);
      setCarwashShopManuallySelected(false);
      setTraditionalRefuelFlowStage("selection");
      setTraditionalRefuelCardCollapsed(false);
      setFuelStationManuallySelected(false);
      setTraditionalParcelFlowStage("selection");
      setTraditionalParcelCardCollapsed(false);
    }
  }, [state.desktop]);

  useEffect(() => {
    if (state.desktop !== "standard" || !voiceDestinationSearchRequest) {
      return;
    }

    setDestinationSearchServiceId(voiceDestinationSearchRequest.serviceId);

    if (voiceDestinationSearchRequest.serviceId === "charging") {
      setTraditionalDestinationTarget("restaurant");
      setTraditionalChargingFlowStage("selection");
      setTraditionalCardCollapsed(false);
    }

    if (voiceDestinationSearchRequest.serviceId === "carwash") {
      setTraditionalCarwashFlowStage("selection");
      setTraditionalCarwashCardCollapsed(false);
    }

    if (voiceDestinationSearchRequest.serviceId === "refuel") {
      setTraditionalRefuelFlowStage("selection");
      setTraditionalRefuelCardCollapsed(false);
    }
  }, [state.desktop, voiceDestinationSearchRequest]);

  if (state.desktop === "standard") {
    return (
      <main className="desktop-stage desktop-stage--standard desktop-stage--figma-replica">
        <CarScreenViewport>
          <div className="desktop-stage__screen-canvas desktop-stage__screen-canvas--traditional">
            <div className="traditional-desktop-layer" aria-hidden="true">
              <img
                className="traditional-desktop-layer__source"
                src={traditionalDesktopSrc}
                alt=""
                draggable={false}
              />
            </div>
            {hasTraditionalNavigation ? (
              <div className="traditional-desktop-map-overlay" aria-label="传统桌面地图导航">
                {!shouldShowTraditionalChargingCard && !shouldShowTraditionalParcelCard && !shouldShowTraditionalCarwashCard && !shouldShowTraditionalRefuelCard && traditionalNavigationSrc ? (
                  <img
                    className="traditional-desktop-map-overlay__image"
                    src={traditionalNavigationSrc}
                    alt=""
                    draggable={false}
                    aria-hidden="true"
                  />
                ) : null}
                {shouldShowTraditionalChargingCard ? (
                  <>
                    <TraditionalDesktopAmapLayer
                      destinationLocation={destinationLocation}
                      destinationName={destinationName}
                      mode="charging"
                      routeGeometry={routeGeometry}
                      stations={chargingStations}
                      selectedStation={selectedChargingStation}
                      source={chargingStationSource}
                      target={routeDestinationTarget}
                      vehicle={displayedVehicle}
                      onRefreshVehicleLocation={cockpitData.vehicle.refreshLocation}
                      onSelectStation={(stationId) => {
                        setSelectedChargingStationId(stationId);
                        setChargingStationManuallySelected(true);
                        setTraditionalDestinationTarget("charging");
                        setTraditionalChargingFlowStage("selection");
                        setTraditionalCardCollapsed(false);
                      }}
                    />
                    <TraditionalDesktopMapNavigationCard
                      batteryLevel={state.batteryLevel}
                      collapsed={traditionalCardCollapsed}
                      destinationName={destinationName}
                      flowStage={traditionalChargingFlowStage}
                      selectedStation={selectedChargingStation}
                      selectedStationId={selectedChargingStationId}
                      stations={chargingStations}
                      target={routeDestinationTarget}
                      onArriveCharging={() => {
                        setTraditionalDestinationTarget("charging");
                        setTraditionalChargingFlowStage("arrivedCharging");
                        setTraditionalCardCollapsed(false);
                      }}
                      onContinueDestination={() => {
                        setTraditionalDestinationTarget("restaurant");
                        setTraditionalChargingFlowStage("toDestination");
                        setTraditionalCardCollapsed(true);
                      }}
                      onExpand={() => {
                        setTraditionalCardCollapsed(false);
                        if (traditionalChargingFlowStage !== "arrivedCharging") {
                          setTraditionalChargingFlowStage("selection");
                        }
                      }}
                      onGoCharge={() => {
                        setTraditionalDestinationTarget("charging");
                        setTraditionalChargingFlowStage("toCharging");
                        setTraditionalCardCollapsed(true);
                        onStartChargingSession(selectedChargingStation);
                      }}
                      onGoDestination={() => {
                        setTraditionalDestinationTarget("restaurant");
                        setTraditionalChargingFlowStage("directDestination");
                        setTraditionalCardCollapsed(true);
                      }}
                      onSelectStation={(stationId) => {
                        setSelectedChargingStationId(stationId);
                        setChargingStationManuallySelected(true);
                        setTraditionalDestinationTarget("charging");
                        setTraditionalChargingFlowStage("selection");
                        setTraditionalCardCollapsed(false);
                      }}
                    />
                  </>
                ) : null}
                {shouldShowTraditionalCarwashCard ? (
                  <>
                    <TraditionalDesktopAmapLayer
                      destinationLocation={destinationLocation}
                      destinationName={destinationName}
                      mode="carwash"
                      carwashShops={carwashShops}
                      selectedCarwashShop={selectedCarwashShop}
                      routeGeometry={routeGeometry}
                      stations={chargingStations}
                      selectedStation={selectedChargingStation}
                      source={carwashShopSource}
                      target={routeDestinationTarget}
                      vehicle={displayedVehicle}
                      onRefreshVehicleLocation={cockpitData.vehicle.refreshLocation}
                      onSelectCarwashShop={(shopId) => {
                        setSelectedCarwashShopId(shopId);
                        setCarwashShopManuallySelected(true);
                        setTraditionalCarwashFlowStage("selection");
                        setTraditionalCarwashCardCollapsed(false);
                      }}
                      onSelectStation={() => undefined}
                    />
                    <TraditionalDesktopCarwashNavigationCard
                      collapsed={traditionalCarwashCardCollapsed}
                      destinationName={destinationName}
                      destinationSearch={hasDestinationSearchRoute}
                      flowStage={traditionalCarwashFlowStage}
                      selectedShop={selectedCarwashShop}
                      selectedShopId={selectedCarwashShop.id}
                      shops={carwashShops}
                      onArriveCarwash={() => {
                        onCarwashArriveShop(selectedCarwashShop);
                      }}
                      onExpand={() => {
                        setTraditionalCarwashCardCollapsed(false);
                        if (traditionalCarwashFlowStage !== "arrivedCarwash") {
                          setTraditionalCarwashFlowStage("selection");
                        }
                      }}
                      onGoCarwash={() => {
                        setTraditionalCarwashFlowStage("toCarwash");
                        setTraditionalCarwashCardCollapsed(true);
                      }}
                      onSelectShop={(shopId) => {
                        setSelectedCarwashShopId(shopId);
                        setCarwashShopManuallySelected(true);
                        setTraditionalCarwashFlowStage("selection");
                        setTraditionalCarwashCardCollapsed(false);
                      }}
                    />
                  </>
                ) : null}
                {shouldShowTraditionalRefuelCard ? (
                  <>
                    <TraditionalDesktopAmapLayer
                      destinationLocation={destinationLocation}
                      destinationName={destinationName}
                      mode="refuel"
                      fuelStations={fuelStations}
                      selectedFuelStation={selectedFuelStation}
                      routeGeometry={routeGeometry}
                      stations={chargingStations}
                      selectedStation={selectedChargingStation}
                      source={fuelStationSource}
                      target={routeDestinationTarget}
                      vehicle={displayedVehicle}
                      onRefreshVehicleLocation={cockpitData.vehicle.refreshLocation}
                      onSelectFuelStation={(stationId) => {
                        setSelectedFuelStationId(stationId);
                        setFuelStationManuallySelected(true);
                        setTraditionalRefuelFlowStage("selection");
                        setTraditionalRefuelCardCollapsed(false);
                      }}
                      onSelectStation={() => undefined}
                    />
                    <TraditionalDesktopRefuelNavigationCard
                      collapsed={traditionalRefuelCardCollapsed}
                      destinationName={destinationName}
                      destinationSearch={hasDestinationSearchRoute}
                      flowStage={traditionalRefuelFlowStage}
                      selectedStation={selectedFuelStation}
                      selectedStationId={selectedFuelStation.id}
                      stations={fuelStations}
                      onArriveRefuel={() => {
                        onRefuelArriveStation(selectedFuelStation);
                      }}
                      onExpand={() => {
                        setTraditionalRefuelCardCollapsed(false);
                        if (traditionalRefuelFlowStage !== "arrivedRefuel") {
                          setTraditionalRefuelFlowStage("selection");
                        }
                      }}
                      onGoRefuel={() => {
                        setTraditionalRefuelFlowStage("toRefuel");
                        setTraditionalRefuelCardCollapsed(true);
                      }}
                      onSelectStation={(stationId) => {
                        setSelectedFuelStationId(stationId);
                        setFuelStationManuallySelected(true);
                        setTraditionalRefuelFlowStage("selection");
                        setTraditionalRefuelCardCollapsed(false);
                      }}
                    />
                  </>
                ) : null}
                {shouldShowTraditionalParcelCard ? (
                  <>
                    <TraditionalDesktopAmapLayer
                      destinationLocation={destinationLocation}
                      destinationName={destinationName}
                      mode="parcel"
                      homeLabel={cockpitData.home.name}
                      homeLocation={cockpitData.home.location}
                      parcelPoints={parcelPickupPoints}
                      selectedParcelPoint={selectedParcelPoint}
                      routeGeometry={routeGeometry}
                      stations={chargingStations}
                      selectedStation={selectedChargingStation}
                      source={parcelPointSource}
                      target="parcel"
                      vehicle={displayedVehicle}
                      onRefreshVehicleLocation={cockpitData.vehicle.refreshLocation}
                      onSelectParcelPoint={(pointId) => {
                        setSelectedParcelPointId(pointId);
                        setTraditionalParcelFlowStage("selection");
                        setTraditionalParcelCardCollapsed(false);
                      }}
                      onSelectStation={() => undefined}
                    />
                    <TraditionalDesktopParcelNavigationCard
                      collapsed={traditionalParcelCardCollapsed}
                      flowStage={traditionalParcelFlowStage}
                      pendingCount={cockpitData.parcel.pendingCount}
                      selectedPickupPoint={selectedParcelPoint}
                      onArrivePickup={() => {
                        setTraditionalParcelFlowStage("arrivedPickup");
                        setTraditionalParcelCardCollapsed(false);
                      }}
                      onArriveHome={() => {
                        onParcelArriveHome({
                          pendingCount: cockpitData.parcel.pendingCount || selectedParcelPoint.pendingCount,
                          pickupPointName: getPickupPointDisplayName(selectedParcelPoint)
                        });
                      }}
                      onExpand={() => {
                        setTraditionalParcelCardCollapsed(false);
                        if (traditionalParcelFlowStage !== "arrivedPickup" && traditionalParcelFlowStage !== "toHome") {
                          setTraditionalParcelFlowStage("selection");
                        }
                      }}
                      onGoHome={() => {
                        setTraditionalParcelFlowStage("toHome");
                        setTraditionalParcelCardCollapsed(true);
                      }}
                      onGoPickup={() => {
                        setTraditionalParcelFlowStage("toPickup");
                        setTraditionalParcelCardCollapsed(true);
                      }}
                      onOpenDetail={() => setParcelDetailOpen(true)}
                    />
                  </>
                ) : null}
              </div>
            ) : null}
            <NightStatusBar
              batteryLevel={state.batteryLevel}
              rangeKm={state.rangeKm}
              fuelLevel={state.fuelLevel}
              fuelRangeKm={state.fuelRangeKm}
              time={displayedTime}
              avatarSrc={avatarSrc}
              topDockSrc={topDockSrc}
            />
            <NightDock dockSrc={traditionalDockSrc} musicIconSrc={null} label="传统桌面底部 Dock" />
            {parcelDetailOpen ? (
              <ParcelDetailPopup onClose={() => setParcelDetailOpen(false)} />
            ) : null}
            <NightVoiceCaptionOverlay evaSrc={evaFrontSrc} voiceCaption={voiceCaption} />
          </div>
        </CarScreenViewport>
      </main>
    );
  }

  return (
    <main className="desktop-stage desktop-stage--ai desktop-stage--figma-replica">
      <CarScreenViewport>
        <div className="desktop-stage__screen-canvas">
          <NightHeroLayer
            time={displayedTime}
            backgroundMode="night"
            wallpaperSrc={wallpaperSrc}
            evaSrc={evaFrontSrc}
            evaExpandedSrc={evaLookDownSrc}
            evaAlertSrc={evaAlertSrc}
            hasPrompt={hasNightPrompt}
            isCardExpanded={isNightCardExpanded}
          />
          <NightStatusBar
            batteryLevel={state.batteryLevel}
            rangeKm={state.rangeKm}
            fuelLevel={state.fuelLevel}
            fuelRangeKm={state.fuelRangeKm}
            time={displayedTime}
            avatarSrc={avatarSrc}
            topDockSrc={topDockSrc}
          />
          <NightFloatingShortcut shortcutSrc={floatingShortcutSrc} />
          {isAudioCompanionVisible && audioCompanionDemo ? (
            <NightAudioCompanionDemo
              state={audioCompanionDemo}
              onAnswerFinished={onAudioCompanionAnswerFinished}
              onBackToRecommend={onAudioCompanionBackToRecommend}
              onExpandPreview={onAudioCompanionExpandPreview}
              onFallbackSpeak={onAudioCompanionFallbackSpeak}
              onPreviewProgram={onAudioCompanionPreviewProgram}
              onSelectProgram={onAudioCompanionSelectProgram}
              onStartGeneratedProgram={onAudioCompanionStartGeneratedProgram}
            />
          ) : null}
          {isVideoCompanionVisible && videoCompanionDemo ? (
            <NightVideoCompanionLayer
              evaSrc={evaFrontSrc}
              state={videoCompanionDemo}
              onActorFinished={onVideoCompanionActorFinished}
              onAskActor={onVideoCompanionAskActor}
              onOpenQuicklook={onVideoCompanionOpenQuicklook}
              onPlaybackEnded={onVideoCompanionPlaybackEnded}
              onReset={onVideoCompanionReset}
              onSelectVideo={onVideoCompanionSelectVideo}
            />
          ) : null}
          {!isCompanionVisible && !accidentGuideOpen && !selectedDecision && !isCarwashModeVisible && !isChargingSessionVisible && !isParcelExitReminderVisible && !isRefuelAuthorizationVisible ? (
            <NightAiCard
              summary={summary}
              selectedService={selectedService}
              carwash={cockpitData.carwash}
              chargingStation={selectedChargingStation}
              chargingStationCount={chargingStations.length}
              chargingStationSource={chargingStationSource}
              destinationName={destinationName}
              fuel={cockpitData.fuel}
              parcel={cockpitData.parcel}
              onOpenService={onOpenService}
            />
          ) : null}
          {!isCompanionVisible && selectedDecision && !accidentGuideOpen && !isCarwashModeVisible && !isChargingSessionVisible && !isRefuelAuthorizationVisible ? (
            <NightAiExpandedCard
              decision={selectedDecision}
              state={state}
              carwash={cockpitData.carwash}
              chargingStation={selectedChargingStation}
              chargingStationCount={chargingStations.length}
              chargingStationSource={chargingStationSource}
              destinationName={destinationName}
              fuel={cockpitData.fuel}
              parcel={cockpitData.parcel}
              parkingPaymentOpen={parkingPaymentOpen}
              onClose={onCloseService}
              onConfirm={onConfirmService}
              onCancelParkingPayment={onCloseParkingPayment}
              onOpenParcelDetail={() => setParcelDetailOpen(true)}
              onParkingPaymentPaid={onParkingPaymentPaid}
            />
          ) : null}
          {!isCompanionVisible && isParcelExitReminderVisible && !accidentGuideOpen && !selectedDecision && !isCarwashModeVisible && !isChargingSessionVisible && !isRefuelAuthorizationVisible ? (
            <NightParcelExitReminderCard
              reminder={parcelExitReminder}
              onDismiss={onDismissParcelExitReminder}
              onOpenDetail={() => setParcelDetailOpen(true)}
            />
          ) : null}
          {!isCompanionVisible && isChargingSessionVisible && !accidentGuideOpen ? (
            <NightChargingSessionCard
              session={chargingSession}
              onConfirmCover={onConfirmChargingCover}
              onDismiss={onDismissChargingSession}
              onEnableRestMode={onEnableChargingRestMode}
              onShowRestRecommendation={onShowChargingRestRecommendation}
            />
          ) : null}
          {!isCompanionVisible && isCarwashModeVisible && !accidentGuideOpen && !selectedDecision && !isChargingSessionVisible ? (
            <NightCarwashModeCard
              session={carwashMode}
              onConfirm={onConfirmCarwashMode}
              onDismiss={onDismissCarwashMode}
            />
          ) : null}
          {!isCompanionVisible && isRefuelAuthorizationVisible && !accidentGuideOpen && !selectedDecision && !isChargingSessionVisible ? (
            <NightRefuelAuthorizationCard
              session={refuelAuthorization}
              onAuthorize={onAuthorizeRefuel}
              onDismiss={onDismissRefuelAuthorization}
            />
          ) : null}
          {!accidentGuideOpen && !isChargingSessionVisible ? (
            <NightBottomCards parkingCardSrc={parkingCardSrc} musicCardSrc={musicCardSrc} />
          ) : null}
          <NightDock />
          {accidentGuideOpen ? (
            <NightAccidentGuideOverlay
              callVisible={accidentCallVisible}
              evaSrc={evaLookDownSrc}
              onConfirmSafe={onConfirmAccidentGuide}
              onHangupCall={onHangupAccidentCall}
            />
          ) : null}
          {parcelDetailOpen ? (
            <ParcelDetailPopup onClose={() => setParcelDetailOpen(false)} />
          ) : null}
          <NightVoiceCaptionOverlay evaSrc={evaFrontSrc} voiceCaption={voiceCaption} />
        </div>
      </CarScreenViewport>
    </main>
  );
}

function createStationVehicleState(
  vehicle: CockpitData["vehicle"],
  location: GeoPoint
) {
  return {
    ...vehicle,
    vehicleLocation: location,
    locationStatus: "demo" as const,
    locationSource: "demo" as const,
    locationAccuracyM: undefined,
    locationError: undefined
  };
}

function getTraditionalNavigationSrc({
  activeServiceId,
  carwashNavigationSrc,
  chargingNavigationSrc,
  hasCarwashInput,
  hasChargingRoute,
  hasParcelRoute,
  parcelNavigationSrc
}: {
  activeServiceId: ServiceId | null;
  carwashNavigationSrc: string;
  chargingNavigationSrc: string;
  hasCarwashInput: boolean;
  hasChargingRoute: boolean;
  hasParcelRoute: boolean;
  parcelNavigationSrc: string;
}) {
  if (activeServiceId === "parcel" && hasParcelRoute) {
    return parcelNavigationSrc;
  }

  if (activeServiceId === "charging" && hasChargingRoute) {
    return chargingNavigationSrc;
  }

  if (activeServiceId === "carwash" && hasCarwashInput) {
    return carwashNavigationSrc;
  }

  if (hasParcelRoute) {
    return parcelNavigationSrc;
  }

  if (hasCarwashInput) {
    return carwashNavigationSrc;
  }

  if (hasChargingRoute) {
    return chargingNavigationSrc;
  }

  return null;
}
